import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MAvatar from "@/components/ui/atoms/MAvatar";
import MBadge from "@/components/ui/atoms/MBadge";
import MDialog from "@/components/ui/atoms/MDialog";
import MaatraLogo from "@/components/ui/atoms/MaatraLogo";
import { IconPlus, IconUsers } from "@tabler/icons-react";
import { useFamily } from "@/hooks/useFamily";
import { getMemberInfo } from "@/hooks/useMembers";
import { usePendingAccessRequests } from "@/hooks/usePendingAccessRequests";
import { useMemberAccess } from "@/hooks/useMemberAccess";
import styles from "./FamilyWidget.module.css";

interface FamilyWidgetProps {
  isExperienced?: boolean;
  /** Notifies the parent whenever the family members list finishes/starts loading. */
  onLoadingChange?: (loading: boolean) => void;
}

const FamilyWidget: React.FC<FamilyWidgetProps> = ({
  isExperienced = true,
  onLoadingChange,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const memberInfo = getMemberInfo(true);
  const loggedInMember = getMemberInfo(true);
  const {
    data: familyMembers = [],
    managedIds,
    loading: familyMembersLoading,
  } = useFamily();

  React.useEffect(() => {
    onLoadingChange?.(familyMembersLoading);
  }, [familyMembersLoading, onLoadingChange]);
  const { pendingRequests, refetch: refetchPendingRequests } =
    usePendingAccessRequests({
      familyMembers,
      managedIds,
      familyMemberType: memberInfo.family_member_type,
      enabled: true,
    });
  const {
    handleMemberClick: handleMemberClickFromHook,
    handleRequestAccessWithCleanup,
    requestingAccess,
  } = useMemberAccess();
  const [showNoAccessDialog, setShowNoAccessDialog] = useState(false);
  const [showConsentDialog, setShowConsentDialog] = useState(false);
  const [selectedMemberForAccess, setSelectedMemberForAccess] =
    useState<any>(null);

  const isOwner = (member: any) => {
    return member.family_member_type === "fo";
  };

  const getMemberDisplayName = (member: any) => {
    const memberId = member.id || member._id;
    const loggedInId = loggedInMember.id || (loggedInMember as any)._id;

    if (memberId === loggedInId) {
      return "You";
    }
    return member.nickname || member.first_name;
  };

  const handleMemberClick = (member: any) => {
    const memberId = member.id || member._id;
    const loggedInId = loggedInMember.id || (loggedInMember as any)._id;

    handleMemberClickFromHook({
      memberId,
      loggedInMemberId: loggedInId,
      loggedInMemberType: loggedInMember.family_member_type || "",
      managedIds,
      managedLoading: false, // Add this line
      onNoAccess: () => {
        setSelectedMemberForAccess(member);
        setShowNoAccessDialog(true);
      },
      backUrl: "/dashboard", // Add backUrl for dashboard navigation
    });
  };

  const handleRequestAccess = () => {
    if (!selectedMemberForAccess) return;

    setShowNoAccessDialog(false);
    setShowConsentDialog(true);
  };

  const handleConfirmRequestAccess = async () => {
    if (!selectedMemberForAccess) return;

    const memberId = loggedInMember.id || (loggedInMember as any)._id;
    const familyMemberId =
      selectedMemberForAccess.id || selectedMemberForAccess._id;
    const familyId = memberInfo.current_family;

    await handleRequestAccessWithCleanup(
      { memberId, familyId, familyMemberId },
      () => {
        setShowConsentDialog(false);
        setSelectedMemberForAccess(null);
        // Refetch pending requests to update the UI
        console.log(
          "FamilyWidget: Calling refetchPendingRequests after successful access request"
        );
        // Add a small delay to allow backend to process the request
        setTimeout(() => {
          console.log("FamilyWidget: Executing delayed refetch");
          refetchPendingRequests();
        }, 1000);
      }
    );
  };

  return (
    <>
      <div
        className={styles.familyWidgetSection}
        style={!isExperienced ? { width: "100%", position: "relative" } : {}}
        data-family-widget
      >
        {(() => {
          const sortedMembers = [...familyMembers].sort((a, b) => {
            const aId = a.id || a._id;
            const bId = b.id || b._id;
            const loggedInId = loggedInMember.id || (loggedInMember as any)._id;

            // Logged-in user always first
            if (aId === loggedInId) return -1;
            if (bId === loggedInId) return 1;

            // Sort others alphabetically by first name
            const aName = (a.first_name || "").toLowerCase();
            const bName = (b.first_name || "").toLowerCase();
            return aName.localeCompare(bName);
          });

          return (
            <>
              <h2 className={styles.compactTitle}>Your family</h2>
              <div className={styles.compactCard}>
                <div className={styles.compactHeaderRow}>
                  <div className={styles.compactMemberCount}>
                    <IconUsers size={24} color="#606060" />
                    <span>
                      {t("dashboard.familyWidget.member", {
                        count: sortedMembers.length,
                      })}
                    </span>
                  </div>
                  <button
                    type="button"
                    className={styles.compactTapToView}
                    onClick={() => handleMemberClick(loggedInMember)}
                  >
                    {t("dashboard.familyWidget.tapToViewProfile")}
                  </button>
                </div>
                <div className={styles.compactAvatarRow}>
                  {sortedMembers.map((member: any) => {
                    const memberId = member.id || member._id;
                    const owner = isOwner(member);
                    const displayName = getMemberDisplayName(member);
                    const hasCheckmark = member.registered_member && !owner;

                    return (
                      <button
                        key={memberId}
                        className={styles.compactMemberTile}
                        onClick={() => handleMemberClick(member)}
                        type="button"
                      >
                        <div className={styles.avatarWrapper}>
                          <MAvatar
                            size="lg"
                            type={member.avatar ? "dp" : "no-dp"}
                            src={member.avatar}
                            text={`${member.first_name || ""}${
                              member.last_name ? " " + member.last_name : ""
                            }`.trim()}
                            textSize={24}
                            className={
                              member.registered_member || owner
                                ? styles.avatarRegistered
                                : styles.avatarUnregistered
                            }
                          />
                          {owner && (
                            <MBadge className={styles.compactOwnerBadge}>
                              {t("dashboard.familyWidget.owner")}
                            </MBadge>
                          )}
                          {hasCheckmark && (
                            <div className={styles.checkmarkBadge}>
                              <MaatraLogo size={12} fill="#FBFBFB" />
                            </div>
                          )}
                        </div>
                        <div className={styles.compactMemberName}>
                          {displayName}
                        </div>
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    className={styles.compactMemberTile}
                    onClick={() => navigate("/profile/family/add-member")}
                    data-add-member-button
                  >
                    <div className={styles.addMemberAvatar}>
                      <IconPlus size={24} color="#4C189A" />
                    </div>
                    <div className={styles.addMemberLabel}>
                      {t("dashboard.familyWidget.addMember")}
                    </div>
                  </button>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* No Access Dialog */}
      <MDialog
        isOpen={showNoAccessDialog}
        onClose={() => {
          setShowNoAccessDialog(false);
          setSelectedMemberForAccess(null);
        }}
        title="No profile access"
        description="You don't have access to this family member's profile. You can ask the Family Owner for access if you need it."
        primaryCtaText={
          pendingRequests[
            selectedMemberForAccess?.id || selectedMemberForAccess?._id || ""
          ]
            ? "Access Requested"
            : "Ask for access"
        }
        secondaryCtaText="Cancel"
        onPrimaryCta={handleRequestAccess}
        onSecondaryCta={() => {
          setShowNoAccessDialog(false);
          setSelectedMemberForAccess(null);
        }}
        isPrimaryCtaDisabled={
          pendingRequests[
            selectedMemberForAccess?.id || selectedMemberForAccess?._id || ""
          ]
        }
      />

      {/* Consent Confirmation Dialog */}
      <MDialog
        isOpen={showConsentDialog}
        onClose={() => {
          setShowConsentDialog(false);
          setSelectedMemberForAccess(null);
        }}
        title="Consent needed"
        description="Please confirm that you have this family member's consent to view their health information."
        primaryCtaText="Confirm"
        secondaryCtaText="Cancel"
        onPrimaryCta={handleConfirmRequestAccess}
        onSecondaryCta={() => {
          setShowConsentDialog(false);
          setSelectedMemberForAccess(null);
        }}
        isPrimaryCtaLoading={requestingAccess}
      />
    </>
  );
};

export default FamilyWidget;
